<template>
  <div :class="mainCssClass">
    <alert
      v-if="alertProps"
      :message="alertProps.message"
      :type="alertProps.type"
      :dismissable="alertProps.dismissable"
      @dismiss="onAlertDismiss"
    />
    <loading-indicator
      v-if="isInitialLoading"
      :message="currentLoadingMessage"
    />
    <slot name="before-grid" />
    <slot name="content">
      <div
        v-if="gridVisible"
        :class="gridWrapperCssClass"
      >
        <slot
          v-if="headerVisible"
          name="before-header"
        />
        <div
          v-if="headerVisible"
          :class="[gridHeaderCssClass, 'btn-wrapper']"
        >
          <slot name="header">
            <grid-header-action-view
              v-if="newHeaderViewOpt"
              :actions="headerActions"
              :selected-item-ids="selectedItemIds"
              @item-action="performHeaderAction"
            />
            <template v-else>
              <template
                v-for="(action, actionIdx) in headerActions"
                :key="actionIdx"
              >
                <div
                  v-if="headerActionWithSubActions(action)"
                  class="btn-group me-2"
                >
                  <a
                    v-if="action.type == 'action'"
                    :class="['btn btn-sm', headerActionCssClass(action)]"
                    href="#"
                    @click.prevent="performHeaderAction(action)"
                  >
                    {{ action.title }}
                  </a>
                  <a
                    v-else
                    :href="action.url"
                    :class="['btn btn-sm', headerActionCssClass(action)]"
                  >
                    {{ action.title }}
                  </a>
                  <button
                    type="button"
                    :class="['btn btn-sm', headerActionCssClass(action), 'dropdown-toggle dropdown-toggle-split']"
                    data-bs-toggle="dropdown"
                    aria-haspopup="true"
                    aria-expanded="false"
                  >
                    <span class="visually-hidden">Toggle Dropdown</span>
                  </button>
                  <div class="dropdown-menu">
                    <template
                      v-for="(subAction, subActionIdx) of action.sub_actions || []"
                      :key="subActionIdx"
                    >
                      <a
                        v-if="subAction.type == 'action'"
                        class="dropdown-item"
                        href="#"
                        @click.prevent="performHeaderAction(subAction)"
                      >
                        {{ subAction.title }}
                      </a>
                      <a
                        v-else
                        :href="subAction.url"
                        class="dropdown-item"
                      >
                        {{ subAction.title }}
                      </a>
                    </template>
                  </div>
                </div>
                <a
                  v-else-if="action.type == 'action'"
                  :class="['btn btn-sm', headerActionCssClass(action), 'me-2']"
                  href="#"
                  @click.prevent="performHeaderAction(action)"
                >
                  {{ action.title }}
                </a>
                <a
                  v-else
                  :href="action.url"
                  :class="['btn btn-sm', headerActionCssClass(action), 'me-2']"
                >
                  {{ action.title }}
                </a>
              </template>
            </template>

            <div
              v-if="hasAdditionalHeaderActions"
              class="dropdown additional-header-actions"
            >
              <button
                id="customActionsDropdownMenuButton"
                type="button"
                class="btn btn-sm btn-success"
                data-bs-toggle="dropdown"
                aria-haspopup="true"
                aria-expanded="false"
              >
                <span class="glyphicons menu-hamburger align-middle" />
              </button>
              <div
                class="dropdown-menu dropdown-menu-end"
                aria-labelledby="customActionsDropdownMenuButton"
              >
                <template
                  v-for="(action, actionIdx) in additionalHeaderActions"
                  :key="actionIdx"
                >
                  <a
                    v-if="action.type === 'url'"
                    class="dropdown-item"
                    :href="action.url"
                    :target="action.target"
                  >
                    {{ action.title }}
                  </a>
                  <div
                    v-else-if="action.title === '---' || action.type === 'separator'"
                    class="dropdown-divider"
                  />
                  <a
                    v-else
                    class="dropdown-item"
                    href="#"
                    @click.prevent="performHeaderAction(action)"
                  >
                    {{ action.title }}
                  </a>
                </template>
              </div>
            </div>
          </slot>
        </div>
        <grid-filters
          v-if="hasFilters"
          :filters="filters"
          @selected="onGridFilterChange"
        />
        <div
          v-if="showTotalRecords"
          class="meta-row"
        >
          Total: {{ totalRecords }} records
        </div>
        <slot name="before-table" />
        <loading-indicator
          v-if="alwaysShowLoadingIndicator && !isInitialLoading && isLoading"
          :message="currentLoadingMessage"
          additional-css-class="mt-3"
        />
        <slot
          v-if="!alwaysShowLoadingIndicator || !isLoading"
          name="table"
          :data="data"
          :columns="columns"
        >
          <div
            v-if="hasItems"
            class="table-responsive"
          >
            <table :class="[{ 'table table-striped table-bordered table-hover table-sm resources-table': true, 'loading': isLoading }, gridTableClass]">
              <thead>
                <tr>
                  <th
                    v-if="addCheckmarks"
                    class="tight"
                  >
                    <input
                      ref="toggleAll"
                      v-model="checkAll"
                      type="checkbox"
                    >
                  </th>
                  <th
                    v-for="column in columns"
                    :key="column.name"
                    :class="fHeaderClass(column)"
                    @click.prevent="columnAction(column)"
                  >
                    {{ column.title }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <template
                  v-for="(item, itemIdx) in data"
                  :key="item.id"
                >
                  <tr :class="fRowClass(item)">
                    <td
                      v-if="addCheckmarks"
                      class="select_cb"
                    >
                      <input
                        ref="itemCb"
                        v-model="selectedItemIds"
                        :value="item.id"
                        type="checkbox"
                      >
                    </td>
                    <td
                      v-for="column in columns"
                      :key="column.name"
                      :class="fCellClass(column)"
                    >
                      <template v-if="column.name == ':actions'">
                        <slot
                          name="action-cell"
                          :item="item"
                        >
                          <template v-if="newItemActionView">
                            <grid-item-action-view
                              v-for="(itemAction, itemActionIdx) of itemActions(item)"
                              :key="itemActionIdx"
                              :item="item"
                              :action="itemAction"
                              :dropdown-align-right="itemDropdownAlignRight"
                              @item-action="triggerItemAction"
                            />
                          </template>
                          <item-action-view
                            v-else
                            :item="item"
                            :edit-action-as-link="editActionAsLink"
                            @item-action="triggerItemAction"
                          />
                        </slot>
                      </template>
                      <template v-else-if="column.name == ':idx'">
                        {{ firstIndexInPage + itemIdx }}
                      </template>
                      <template v-else-if="column.type == 'custom'">
                        <slot
                          name="custom-cell"
                          :item="item"
                          :column="column"
                        >
                          {{ serializeToJsona ? item[column.name] : item.attributes[column.name] }}
                        </slot>
                      </template>
                      <template v-else-if="column.type == 'link_to'">
                        <a :href="itemLink(item, column)">{{ serializeToJsona ? item[column.name] : item.attributes[column.name] }}</a>
                      </template>
                      <template v-else>
                        {{ serializeToJsona ? item[column.name] : item.attributes[column.name] }}
                      </template>
                    </td>
                  </tr>
                  <slot
                    name="additional-row"
                    :item="item"
                  />
                </template>
              </tbody>
            </table>
          </div>
          <div
            v-else
            class="alert alert-info no-items"
          >
            There are no items to display
          </div>
          <grid-pager
            v-if="showPager"
            :pages="pages"
            :current-page="currentPage"
            @change-page="changePage"
          />
        </slot>
      </div>
    </slot>
    <teleport
      to="#vue-portal-target"
      append
    >
      <modal-confirmation
        ref="gridConfirmationModal"
        @modal-hidden="onGridConfirmationModalClosed"
      />
      <remote-modal
        ref="gridRemoteFormModal"
        :modal-form="true"
        modal-title="..."
        @modal-hidden="onGridRemoteFormModalClosed"
        @form-success="onGridRemoteFormSuccess"
      />
    </teleport>
  </div>
</template>

<script setup lang="ts">
import {computed, onMounted, type PropType, ref, useTemplateRef, watch} from 'vue'

import axios from 'axios'
import Rails from '@rails/ujs'

import URI from 'urijs'

import Alert from './alert.vue'
import LoadingIndicator from './loading-indicator.vue'
import GridPager from './grid-pager.vue'
import GridHeaderActionView from './grid-header-action-view.vue'
import GridItemActionView from './grid-item-action-view.vue'
import ItemActionView from './item-action-view.vue'
import GridFilters from './grid-filters.vue'
import ModalConfirmation from './modal-confirmation.vue'

import { isPresent, isNonEmptyString, isBlank } from '../js-utils'

import { Jsona, SwitchCaseJsonMapper } from 'jsona'

import { GridActionType, GridFilter, type GridFilterListItem } from './types'
import type { ButtonAction, GridHeaderAction, GridID, GridItemAction } from './types'
import RemoteModal from "@/js/vue-components/remote-modal.vue";
import i18next from "i18next";

interface AlertProps {
  message: string;
  type: string;
  dismissable: boolean;
}

interface GridColumn {
  name: string;
  sortable: boolean;
  sorted: null | 'asc' | 'desc';
  sortKey: string;
  title: string;
  type?: string;
  linkTo?: string;
}

interface CompletedCallbackFn {
  (status: boolean, responseData?: any): void;
}

const props = defineProps({
  newItemActionView: {default: false, type: Boolean},
  showHeader: {default: true, type: Boolean},
  listUrl: {default: null, type: String},
  addCheckmarks: {default: false, type: Boolean},
  gridTableClass: {default: null, type: String},
  editActionAsLink: {default: true, type: Boolean},
  viewActionAsLink: {default: true, type: Boolean},
  handleDelete: {default: true, type: Boolean},
  deleteConfirmMessage: {default: null, type: String},
  resetFilter: {default: false, type: Boolean},
  gridWrapperCssClass: {default: 'card', type: String},
  gridHeaderCssClass: {default: 'card-header grid-header', type: String},
  alwaysShowLoadingIndicator: {default: false, type: Boolean},
  initialFilters: {default: null, type: String},
  performDataFetches: {default: true, type: Boolean},
  newActionTitle: {default: null, type: String},
  serializeToJsona: {default: false, type: Boolean},
  newHeaderView: { type: Boolean },
  newHeaderActionEmit: {default: false, type: Boolean},
  showTotalRecords: {default: false, type: Boolean},
  loadingMessage: {default: null, type: String},
  rowClassFn: {default: null, type: Function as PropType<(item: any) => string | null>},
  headerClassFn: {default: null, type: Function as PropType<(column: any) => string | null>}
})

const newHeaderViewOpt = ref<boolean>(props.newHeaderView || false) // everything should be migrated to this
const initialLoadPerformed = ref<boolean>(false)
const dataLoaded = ref<boolean>(false)
const isLoading = ref<boolean>(false)
const currentLoadingMessage = ref<string | null>(null)
const alertProps = ref<AlertProps | null>(null)
const pages = ref<any[]>([])
const currentPage = ref<number>(1)
const firstIndexInPage = ref<number>(1)
const totalPages = ref<number>(1)
const totalRecords = ref<number>(0)
const columns = ref<GridColumn[]>([])
const data = ref<any[]>([])
const headerActions = ref<GridHeaderAction[]>([])
const additionalHeaderActions = ref<any[]>([])

const loadTimeoutID = ref<number | null>()
const previousLoadKey = ref<string>("")

const sortKey = ref<string | null>(null)
const filters = ref<GridFilter[]>([])
const axiosAbortController = ref<AbortController | null>(null)
const selectedItemIds = ref<GridID[]>([])
const additionalFilters = ref<Record<string, string | string[]> | null>(null)

const gridConfirmationModal = useTemplateRef("gridConfirmationModal")
const gridRemoteFormModal = useTemplateRef<InstanceType<typeof RemoteModal>>("gridRemoteFormModal")

const emit = defineEmits<{
  (e: 'selected-items-changed'): void
  (e: 'will-fetch', listUrl: string, listParams: Record<string, string | string[]>, isInitialLoad: boolean): void
  (e: 'meta-loaded', meta: any): void
  (e: 'links-loaded', links: any): void
  (e: 'data-loaded', responseData: any): void
  (e: 'filters-changed', params: Record<string, string | string[]>): void
  (e: 'item-action', item: any, action: any): void
  (e: 'header-action', actionOrName: GridHeaderAction | ButtonAction | string, url?: string | undefined, action?: GridHeaderAction | ButtonAction | undefined): void
}>()

const mainCssClass = computed((): string => {
  return 'grid'
})

const isInitialLoading = computed((): boolean => {
  return isLoading.value && !initialLoadPerformed.value
})

const headerVisible = computed((): boolean => {
  return props.showHeader && dataLoaded.value
})

const gridVisible = computed((): boolean => {
  return dataLoaded.value && initialLoadPerformed.value
})

const listUrlValid = computed((): boolean => {
  return isNonEmptyString(props.listUrl)
})

const hasFilters = computed((): boolean => {
  return filters.value.length > 0
})

const hasItems = computed((): boolean => {
  return Array.isArray(data.value) && data.value.length > 0
})

const showPager = computed((): boolean => {
  return totalPages.value > 1
})

const checkAll = computed({
  get(): boolean {
    let checkedCount = 0
    for (const item of data.value) {
      if (selectedItemIds.value.includes(item.id)) {
        checkedCount += 1
      }
    }
    return checkedCount > 0 && data.value.length === checkedCount
  },
  set(newValue): void {
    selectedItemIds.value = []
    if (newValue) {
      for (const item of data.value) {
        selectedItemIds.value.push(item.id)
      }
    }
  }
})

const hasAdditionalHeaderActions = computed((): boolean => {
  return Array.isArray(additionalHeaderActions.value) && additionalHeaderActions.value.length > 0
})

const itemDropdownAlignRight = computed((): boolean => {
  if (columns.value.length > 0 && columns.value[0].name == ":actions") {
    return false
  }
  return true
})

watch(selectedItemIds,
  () => {
    emit('selected-items-changed')
  },
  {deep: false}
)

if (!listUrlValid.value) {
  alertProps.value = {
    type: 'danger',
    message: 'Items can\'t be loaded, no `listUrl` set',
    dismissable: false
  }
}

onMounted(() => {
  if (listUrlValid.value) {
    loadItems(props.listUrl)
  }
})

const itemActions = (item: Record<string, any>): GridItemAction[] => {
  const itemActions = item.meta?.actions
  return Array.isArray(itemActions) ? itemActions : []
}

const clearSelectedItemIds = (): void => {
  selectedItemIds.value = []
}

const clearError = (): void => {
  alertProps.value = null
}

const showError = (errorMessage: string): void => {
  alertProps.value = {
    type: 'danger',
    message: errorMessage,
    dismissable: true
  }
}

const showMessage = (message: string): void => {
  alertProps.value = {
    type: 'success',
    message: message,
    dismissable: true
  }
}

const hideMessages = (): void => {
  alertProps.value = null
}

const onAlertDismiss = (): void => {
  hideMessages()
}

const fHeaderClass = (column: any): string => {
  if (props.headerClassFn) {
    return props.headerClassFn(column) ?? ''
  }

  return fHeaderClassBase(column)
}

const fHeaderClassBase = (column: GridColumn, overrideColName?: string): string => {
  let colName = overrideColName || column.name
  if (colName === ':actions') {
    colName = 'table-actions'
  }
  if (column.sortable) {
    if (column.sorted) {
      return `sortable ${column.sorted} ${colName}`
    }
    return `sortable ${colName}`
  }
  return colName
}

const fRowClass = (item: any): string | null => {
  if (props.rowClassFn) {
    return props.rowClassFn(item)
  }

  if (item.meta && isPresent(item.meta.rowCssClass)) {
    return item.meta.rowCssClass
  }
  return null
}

const reload = (): void => {
  previousLoadKey.value = 'reload' // invalidate previouseLoadKey, so that reload is forced
  loadItems(props.listUrl)
}

const getListParams = (): Record<string, string | string[]> => {
  const listParams: Record<string, string | string[]> = {}
  if (sortKey.value) {
    listParams['sort'] = sortKey.value
  }
  // apply additional filters
  if (additionalFilters.value) {
    Object.assign(listParams, additionalFilters.value)
  }
  if (initialLoadPerformed.value) {
    for (const f of filters.value) {
      if (f.hasValue) {
        listParams[f.name] = f.selectedValue
      }
    }
  } else if (isPresent(props.initialFilters)) {
    let filters = null
    try {
      filters = JSON.parse(props.initialFilters)
    } catch (e) {
      filters = null
    }
    if (filters) {
      Object.assign(listParams, filters)
    }
  }
  return listParams
}

const listQueryParams = (includePage: boolean): Record<string, string | string[]> => {
  let listParams = getListParams()
  if (includePage && currentPage.value) {
    listParams['page'] = currentPage.value.toString()
  }
  listParams = Object.assign({}, listParams)
  return listParams
}

const loadItems = (listUrl: string): void => {

  loadTimeoutID.value = null
  clearError()

  const curLoadKey = getLoadKey()
  const isInitialLoad = !initialLoadPerformed.value

  if (!isInitialLoad && previousLoadKey.value == curLoadKey) {
    // already loaded this request
    return
  }

  isLoading.value = true
  currentLoadingMessage.value = props.loadingMessage || 'Loading items ...'

  const listParams = listQueryParams(true)

  // cancel previous load request
  axiosAbortController.value?.abort()
  axiosAbortController.value = new AbortController()

  emit('will-fetch', listUrl, listParams, isInitialLoad)
  if (!props.performDataFetches && initialLoadPerformed.value) {
    // don't fetch subsequent data, instead "parent" will handle the fetch
    return
  }

  // construct list url by applying filter parameters to it, replacing
  // existing filter parameters in listUrl if there are any
  const getListUrl = urlWithParams(listUrl, false, true).toString()

  axios.get(getListUrl, {
    headers: {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'Accept': 'application/vnd.api+json'
    },
    signal: axiosAbortController.value.signal
  })
    .then((response) => {
      previousLoadKey.value = curLoadKey
      parseData(response.data)
      initialLoadPerformed.value = true
    })
    .catch((error) => {
      if (axios.isCancel(error)) {
        // request was canceled
      } else {
        showError(`An error occurred while loading items: ${error.message}`)
      }
    })
    .then(() => {
      isLoading.value = false
      currentLoadingMessage.value = null

      if (!isInitialLoad && curLoadKey != getLoadKey()) {
        // need to check isInitialLoad, otherwise if some filters
        // are set on initial load an extra load will be performed
        window.setTimeout(() => {
          // perform another load
          loadItems(listUrl)
        })
      }
    })
}

const parseData = (responseData: any): void => {
  if (!Array.isArray(responseData.data)) {
    showError('Invalid data received from server. Please refresh page to load data again')
    return
  }
  dataLoaded.value = true
  if (props.serializeToJsona) {
    const result = new Jsona({jsonPropertiesMapper: new SwitchCaseJsonMapper()}).deserialize(responseData)
    if (Array.isArray(result)) {
      data.value = result
    } else {
      data.value = []
    }
  } else {
    data.value = responseData.data.slice()
  }

  let headerActionsSet = false
  if (responseData.meta) {
    if (responseData.meta.newHeaderView == true) {
      newHeaderViewOpt.value = true
    }
    if (Array.isArray(responseData.meta.columns)) {
      columns.value = responseData.meta.columns
    }
    currentPage.value = responseData.meta.currentPage || responseData.meta.current_page
    totalPages.value = responseData.meta.totalPages || responseData.meta.total_pages
    totalRecords.value = responseData.meta.totalItems || responseData.meta.total_items
    pages.value = responseData.meta.pages
    firstIndexInPage.value = responseData.meta.pageFirstIndex || responseData.meta.page_first_index

    const newHeaderActions = responseData.meta.header_actions || responseData.meta.headerActions
    if (Array.isArray(newHeaderActions)) {
      headerActionsSet = true
      headerActions.value = newHeaderActions
    }

    const newAdditionalHeaderActions = responseData.meta.additional_header_actions || responseData.meta.additionalHeaderActions
    if (Array.isArray(newAdditionalHeaderActions)) {
      additionalHeaderActions.value = newAdditionalHeaderActions
    }
  }

  // set selectedItemIds
  if (selectedItemIds.value.length > 0) {
    const newSelectedIds = []
    for (const item of data.value) {
      if (selectedItemIds.value.includes(item.id)) {
        newSelectedIds.push(item.id)
      }
    }
    selectedItemIds.value = newSelectedIds
  }

  if (responseData.meta?.i18n) {
    i18next.addResources(i18next.resolvedLanguage || "en", "translation", responseData.meta.i18n)
  }

  emit('meta-loaded', responseData.meta)
  if (responseData.links) {
    emit('links-loaded', responseData.links)
  }

  if (!headerActionsSet) {
    if (responseData.links && responseData.links.new) {
      headerActions.value = [
        {url: responseData.links.new, title: props.newActionTitle || 'Add new', type: 'link'}
      ]
    } else {
      headerActions.value = []
    }
  }

  if (!initialLoadPerformed.value || props.resetFilter) {
    filters.value = []
    let newFilters = []
    if (responseData.meta && Array.isArray(responseData.meta.filters)) {
      newFilters = responseData.meta.filters
    }

    for (const f of newFilters) {
      const filter = new GridFilter(f)
      if (filter.isValid) {
        filters.value.push(filter)
      }
    }
  }

  emit('data-loaded', responseData)
}

const columnAction = (column: GridColumn): void => {
  if (column.sortable) {
    sortKey.value = null
    if (column.sorted === 'asc') {
      sortKey.value = `-${column.sortKey}`
    } else { //if (column.sorted !== 'desc') {
      sortKey.value = column.sortKey
    }
    loadItems(props.listUrl)
  }
}

const setFilter = (filterName: string, filterValue: any): void => {
  let filterFound = false
  for (const f of filters.value) {
    if (f.name === filterName) {
      filterFound = true
      f.selectedValue = filterValue
    }
  }
  if (!filterFound) {
    if (!additionalFilters.value) {
      additionalFilters.value = {}
    }
    additionalFilters.value[filterName] = filterValue
  }
}

const setFilterParams = (params: any): void => {
  additionalFilters.value = null
  for (const filterName in params) {
    if (params.hasOwnProperty(filterName)) {
      setFilter(filterName, params[filterName])
    }
  }
  reload()
}

const onGridFilterChange = (filter: GridFilter, value: string | string[]): void => {
  const delayedFilter = filter.isText
  filter.selectedValue = value

  if (loadTimeoutID.value) {
    window.clearTimeout(loadTimeoutID.value)
  }

  loadTimeoutID.value = window.setTimeout(() => {
    emit('filters-changed', getListParams())
    loadItems(props.listUrl)
  }, delayedFilter ? 400 : undefined)
}

const getLoadKey = (): string => {
  const filterData = []
  for (const filter of filters.value) {
    if (filter.hasValue) {
      const fd: Record<string, string | string[]> = {}
      fd[filter.name] = filter.selectedValue
      filterData.push(fd)
    }
  }

  if (filterData.length == 0 && isBlank(sortKey.value) && currentPage.value == 1) {
    return ''
  }

  return JSON.stringify({filters: filterData, sort: sortKey.value, page: currentPage.value})
}

const changePage = (pageNr: number): void => {
  if (currentPage.value !== pageNr) {
    currentPage.value = pageNr
    loadItems(props.listUrl)
  }
}

const showLoadingIndicator = (): void => {
  isLoading.value = true
}

const hideLoadingIndicator = (): void => {
  isLoading.value = false
}

const deleteItem = (deleteUrl: string): void => {
  if (confirm(props.deleteConfirmMessage || 'Delete item?')) {
    // this.isLoading = true
    showLoadingIndicator()

    axios.delete(deleteUrl, {
      headers: {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'X-Requested-With': 'XMLHttpRequest',
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'X-CSRF-Token': Rails.csrfToken()
      }
    })
      .then((_response) => {
        reload()
      })
      .catch((error) => {
        showError(`An error occured while deleting item: ${error.message}`)
        hideLoadingIndicator()
      })
  }
}

const triggerItemAction = (item: any, action: any): void => {
  if (props.handleDelete && action && (action.type === 'delete' || ((action.type == 'action' || action.type == 2) && action.name == 'delete'))) {
    deleteItem(action.url)
    return
  }
  if (action && action.type === GridActionType.action && action.name === 'remote-confirmation' && isNonEmptyString(action.url)) {
    gridConfirmationModal.value?.show(action.url)
    return
  }
  if (action && action.type === GridActionType.action && action.name === 'remote-form-modal' && isNonEmptyString(action.url)) {
    const modalOptions = action.options || {}
    gridRemoteFormModal.value?.showModal(action.url, undefined, undefined, modalOptions)
    return
  }
  emit('item-action', item, action)
}

const urlWithParams = (baseUrl: string, filterSelected?: boolean, includePage?: boolean): URI => {
  if (filterSelected !== false) {
    filterSelected = true
  }
  const uri = new URI(baseUrl)
  const urlParams: Record<string, any> = {}

  // URI.js does not properly handle arrays in get params
  // need to construct a special key for arrays
  const qParams = listQueryParams(includePage || false)
  for (const k of Object.keys(qParams)) {
    const v = qParams[k]
    if (Array.isArray(v)) {
      urlParams[`${k}[]`] = v
    } else {
      urlParams[k] = v
    }
  }

  if (filterSelected && selectedItemIds.value.length > 0) {
    urlParams['filter_id[]'] = selectedItemIds.value
  }

  uri.search(Object.assign(uri.search(true), urlParams))
  return uri
}

const exportItems = (exportUrl: string, onlySelected?: boolean): void => {
  window.location.href = urlWithParams(exportUrl, onlySelected).toString()
}

const performHeaderAction = (action: GridHeaderAction | ButtonAction): void => {
  if (props.newHeaderActionEmit || newHeaderViewOpt.value) {
    if (action.name == 'export-xlsx' && isNonEmptyString(action.url)) {
      exportItems(action.url, false)
    } else if (action.name == 'export-xlsx-selected' && isNonEmptyString(action.url)) {
      exportItems(action.url, true)
    } else {
      emit('header-action', action)
    }
  } else {
    emit('header-action', action.name ?? '', action.url, action)
  }
}

// TODO: type for completedCallback
const performRequest = (url: string, params: any, options: any, completedCallback: CompletedCallbackFn): void => {

  const headers: any = {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    'X-Requested-With': 'XMLHttpRequest',
    // eslint-disable-next-line @typescript-eslint/naming-convention
    'X-CSRF-Token': Rails.csrfToken()
  }

  options = Object.assign({
    method: 'post',
    jsonapi: false,
    withLoadingIndicator: true,
    clearError: true,
  }, options || {})

  if (options.jsonapi === true) {
    headers['Accept'] = 'application/vnd.api+json'
  }

  if (options.withLoadingIndicator) {
    showLoadingIndicator()
  }

  if (options.clearError) {
    hideMessages()
  }

  // cancel previous load request
  axiosAbortController.value?.abort()
  axiosAbortController.value = new AbortController()

  axios(url, {
    method: options.method,
    data: params,
    headers: headers,
    signal: axiosAbortController.value.signal,
    onUploadProgress: (progressEvent) => {
      if (options.progressCallback) {
        const total = progressEvent.total ?? progressEvent.loaded
        const uploadProgress = Math.floor(progressEvent.loaded / total * 100)
        options.progressCallback(uploadProgress, progressEvent.loaded === total)
      }
    }
  })
    .then((response) => {
      // this.$emit('requestCompleted', response.data)
      completedCallback(true, response?.data != null ? response.data : null)
    })
    .catch((error) => {
      showError(`An error occured while loading items: ${error.message}`)
      // this.$emit('requestFailed')
      completedCallback(false)
    })
    .then(() => {
      if (options.withLoadingIndicator) {
        hideLoadingIndicator()
      }
    })
}

const headerActionCssClass = (action: GridHeaderAction): string => {
  if (isNonEmptyString(action.css_class)) {
    return action.css_class
  }
  return 'btn-primary'
}

const headerActionWithSubActions = (action: GridHeaderAction): boolean => {
  return Array.isArray(action.sub_actions) && action.sub_actions.length > 0
}

const itemLink = (item: any, column: GridColumn): string => {
  if (isNonEmptyString(column.linkTo) && isNonEmptyString(item.links[column.linkTo])) {
    return item.links[column.linkTo]
  }
  return item.links[column.name]
}

const getItems = (itemIds: GridID[]): any[] => {
  if (itemIds.length == 0) {
    return []
  }

  const items = []
  for (const item of data.value) {
    if (itemIds.includes(item.id)) {
      items.push(item)
    }
  }
  return items
}

const onGridConfirmationModalClosed = (): void => {
  const result = gridConfirmationModal.value?.confirmationResult
  const resultUrl = result?.url
  if (isNonEmptyString(resultUrl)) {
    window.location.href = resultUrl
  }
}

const onGridRemoteFormModalClosed = (closeUrl: string | null): void => {
  if (isNonEmptyString(closeUrl)) {
    window.location.href = closeUrl
  }
}

const onGridRemoteFormSuccess = (): void => {
  gridRemoteFormModal.value?.hideModal()
  reload()
}

const fCellClass = (column: any): string => {
  const result = []
  let addName = true

  if (column.name === ':actions') {
    addName = false
    result.push('table-actions')
  } else if (column.name === ':idx') {
    addName = false
    result.push('tight idx')
  }

  if (addName) {
    result.push(column.name)
  }

  if (isNonEmptyString(column.cssClass)) {
    result.push(column.cssClass)
  }

  if (isNonEmptyString(column.css_class)) {
    result.push(column.css_class)
  }

  return result.join(' ')
}

defineExpose({
  clearSelectedItemIds,
  clearError,
  columns,
  showError,
  showMessage,
  reload,
  setFilter,
  setFilterParams,
  showLoadingIndicator,
  hideLoadingIndicator,
  performRequest,
  getItems,
  fHeaderClassBase,
  checkAll,
  selectedItemIds,
  listQueryParams,
  exportItems,
  triggerItemAction,
  urlWithParams,
  get data() {
    return data.value
  }
})

</script>
